import React, { useState } from 'react';
import { Box, Grid, GridList, GridListTile, Typography, useTheme } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import { CompanyIcon } from 'ui/icons/companyIcon/CompanyIcon';
import { useLocale } from 'hooks/useLocale/useLocale';
import { Button, IconTile, Loader, Page } from 'ui';
import { AppRoute } from 'routing/AppRoute.enum';
import { HomeProps, LoginScreens } from 'app/home/Home.types';
import { getAccountTypes } from 'config/accountTypes';
import { Form } from 'form/form/Form';
import { GenericField } from 'form/fields';

import { useStyles } from './Home.styles';

export const Home = ({
  onLogin,
  showRegister,
  isLoading,
  onLoginWithRole,
  onLoginWithKey,
  onLoginWithBebatRole,
  errorMessageId,
}: HomeProps) => {
  const classes = useStyles();
  const { formatMessage } = useLocale();
  const { push } = useHistory();
  const [loginScreen, setLoginScreen] = useState(LoginScreens.LOGIN_OPTIONS);
  const theme = useTheme();
  const types = getAccountTypes(theme);

  const handleRegister = () => {
    push(AppRoute.accountType);
  };

  const loginOptions = () => {
    return (
      <>
        <Grid item xs={12} lg={3}>
          <Button variant="contained" color="primary" onClick={onLogin} className={classes.button}>
            {formatMessage({ id: 'home.login' })}
          </Button>
        </Grid>
        {onLoginWithRole && (
          <Grid item xs={12} lg={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setLoginScreen(LoginScreens.ACCOUNTS)}
              className={classes.button}
            >
              {formatMessage({ id: 'home.login_with_account' })}
            </Button>
          </Grid>
        )}
        {onLoginWithKey && (
          <Grid item xs={12} lg={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setLoginScreen(LoginScreens.KEY)}
              className={classes.button}
            >
              {formatMessage({ id: 'home.login_with_private_key' })}
            </Button>
          </Grid>
        )}
        {onLoginWithBebatRole && (
          <Grid item xs={12} lg={3}>
            <Button variant="contained" color="primary" onClick={onLoginWithBebatRole} className={classes.button}>
              {formatMessage({ id: 'home.login_as_bebat' })}
            </Button>
          </Grid>
        )}
        {showRegister && (
          <Grid item xs={12}>
            <Button variant="outlined" color="primary" onClick={handleRegister} className={classes.button}>
              {formatMessage({ id: 'home.register' })}
            </Button>
          </Grid>
        )}
      </>
    );
  };

  const accountsScreen = () => {
    return (
      onLoginWithRole && (
        <GridList cols={2} cellHeight="auto" spacing={13}>
          {types.map(type => (
            <GridListTile key={type.label} cols={1}>
              <IconTile
                icon={type.icon}
                title={formatMessage({ id: type.label })}
                color={type.color}
                onClick={() => onLoginWithRole(type.role)}
                iconFullSize
              />
            </GridListTile>
          ))}
        </GridList>
      )
    );
  };

  const keyScreen = () => {
    return (
      onLoginWithKey && (
        <Form>
          {({ handleSubmit }) => (
            <>
              <Grid item xs={12} lg={3}>
                <GenericField name={'key'} label={formatMessage({ id: 'home.key' })} className={classes.input} />
              </Grid>
              <Grid item xs={12} lg={3}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit(onLoginWithKey)}
                  className={classes.button}
                >
                  {formatMessage({ id: 'home.login_with_private_key' })}
                </Button>
              </Grid>
            </>
          )}
        </Form>
      )
    );
  };

  const renderContent = () => {
    const contentByOption = {
      [LoginScreens.LOGIN_OPTIONS]: loginOptions,
      [LoginScreens.KEY]: keyScreen,
      [LoginScreens.ACCOUNTS]: accountsScreen,
    };

    return contentByOption[loginScreen]();
  };

  return (
    <Page
      hideMenu
      hideBack={loginScreen === LoginScreens.LOGIN_OPTIONS || isLoading}
      onBackClick={() => setLoginScreen(LoginScreens.LOGIN_OPTIONS)}
      hideUserInfo
    >
      <Grid container className={classes.container}>
        <Grid item container className={classes.content} spacing={2}>
          {isLoading ? <Loader text={formatMessage({ id: 'home.login_progress' })} /> : renderContent()}
        </Grid>
        {errorMessageId && (
          <Grid item>
            <Typography className={classes.errorMessage}>{formatMessage({ id: errorMessageId })}</Typography>
          </Grid>
        )}
        <Grid item className={classes.footerContainer}>
          <a href="https://www.energyweb.org/" className={classes.link}>
            <Box className={classes.footer}>
              <Typography className={classes.poweredBy}>{formatMessage({ id: 'common.powered_by' })}</Typography>
              <CompanyIcon className={classes.companyIcon} />
              <Typography className={classes.companyName}>{formatMessage({ id: 'common.company_name' })}</Typography>
            </Box>
          </a>
          <Box className={classes.version}>
            <Typography className={classes.poweredBy}>v{process.env.REACT_APP_VERSION}</Typography>
          </Box>
        </Grid>
      </Grid>
    </Page>
  );
};
